import type { ApiBadge, Badge } from "@/features/predictions/types/badge.type";
import type {
  ApiCompetition, ApiPhase, ApiScoringRules, Competition, Phase, ScoringRules,
} from "@/features/predictions/types/competition.type";
import type { ApiLot, Lot } from "@/features/predictions/types/lot.type";
import type {
  ApiMatch, Match, MatchStageType, MatchStatus,
} from "@/features/predictions/types/match.type";
import type {
  ApiParticipantPredictionsView,
  ApiParticipantSummary,
  ParticipantPredictionsView,
  ParticipantSummary,
} from "@/features/predictions/types/participantPredictions.type";
import type { ApiPrediction, Prediction } from "@/features/predictions/types/prediction.type";
import type {
  ApiMyPronosticsPreferences,
  ApiPreferencePatch,
  MyPronosticsPreferences,
  PreferencePatch,
} from "@/features/predictions/types/preferences.type";
import type {
  ApiRanking, ApiRankingEntry, Ranking, RankingEntry,
} from "@/features/predictions/types/ranking.type";
import type {
  ApiStandingEntry, ApiStandingGroup, StandingEntry, StandingGroup,
} from "@/features/predictions/types/standing.type";
import type { ApiMyStats, MyStats } from "@/features/predictions/types/stats.type";
import type { ApiTeam, Team } from "@/features/predictions/types/team.type";
import { resolveBackendUrl } from "@/shared/utils/api.util";

export const toTeam = (api: ApiTeam): Team => ({
  id: api.id,
  name: api.name,
  shortName: api.short_name,
  countryCode: api.country_code,
  flagUrl: api.flag_url,
  logoUrl: api.logo_url ?? null,
  group: api.group,
});

export const toStandingEntry = (api: ApiStandingEntry): StandingEntry => ({
  rank: api.rank,
  pool: api.pool,
  team: toTeam(api.team),
  played: api.played,
  won: api.won,
  drawn: api.drawn,
  lost: api.lost,
  goalsFor: api.goals_for,
  goalsAgainst: api.goals_against,
  goalDifference: api.goal_difference,
  points: api.points,
});

export const toStandingGroup = (api: ApiStandingGroup): StandingGroup => ({
  phase: {
    key: api.phase.key,
    name: api.phase.name,
    kind: api.phase.kind,
    qualificationZones: api.phase.qualification_zones,
  },
  entries: api.entries.map(toStandingEntry),
});

export const toPhase = (api: ApiPhase): Phase => ({
  key: api.key,
  name: api.name,
  kind: api.kind,
  legs: api.legs,
  matchdayCount: api.matchday_count,
  qualificationZones: api.config.qualification_zones ?? [],
});

export const toScoringRules = (api: ApiScoringRules): ScoringRules => ({
  version: api.version,
  rules: api.rules.map((rule) => ({
    key: rule.key,
    category: rule.category ?? "base",
    label: rule.label,
    description: rule.description ?? "",
    points: rule.points,
  })),
  footnotes: api.footnotes ?? [],
  examples: api.examples ?? [],
});

export const toCompetition = (api: ApiCompetition): Competition => ({
  id: api.id,
  name: api.name,
  startsAt: api.starts_at,
  endsAt: api.ends_at,
  currentPhase: api.current_phase,
  isActive: api.is_active,
  status: api.status ?? "active",
  phases: (api.phases ?? []).map(toPhase),
  scoringRules: api.scoring_rules ? toScoringRules(api.scoring_rules) : null,
  editorial: api.editorial ?? null,
  rules: api.rules ?? null,
});

// Normalize backend statuses that aren't part of the frontend enum.
// Backend uses `scored` (finished + points distributed) and `in_progress` (live);
// the UI doesn't need that distinction so we collapse them onto existing buckets.
export const normalizeStatus = (apiStatus: string): MatchStatus => {
  if (apiStatus === "scored") return "finished";
  if (apiStatus === "in_progress") return "live";
  return apiStatus as MatchStatus;
};

// Iso-CdM fallback for API responses that predate `stage_type`.
const deriveStageTypeFallback = (phase: string): MatchStageType => {
  return phase === "group_stage" ? "group_stage" : "knockout";
};

export const toMatch = (api: ApiMatch): Match => ({
  id: api.id,
  competitionId: api.competition_id,
  phase: api.phase,
  matchday: api.matchday ?? null,
  stageType: api.stage_type ?? deriveStageTypeFallback(api.phase),
  label: api.label ?? null,
  group: api.group,
  kickoffAt: api.kickoff_at,
  venueTimezone: api.venue_timezone,
  venueCity: api.venue_city,
  homeTeam: api.home_team ? toTeam(api.home_team) : null,
  awayTeam: api.away_team ? toTeam(api.away_team) : null,
  status: normalizeStatus(api.status),
  isLocked: api.is_locked,
  result: api.result
    ? {
      regularTime: { home: api.result.regular_time.home, away: api.result.regular_time.away },
      // API-Football `goals` = aggregate after extra time (penalty shootout goals
      // not counted). Nullable on the wire until backfill has run.
      goals: api.result.goals?.home !== null && api.result.goals?.home !== undefined
        && api.result.goals.away !== null && api.result.goals.away !== undefined
        ? { home: api.result.goals.home, away: api.result.goals.away }
        : null,
      penaltyShootout: api.result.penalty_shootout
        ? { home: api.result.penalty_shootout.home, away: api.result.penalty_shootout.away }
        : null,
      qualifiedTeamId: api.result.qualified_team_id,
    }
    : null,
});

export const toPrediction = (api: ApiPrediction): Prediction => ({
  id: api.id,
  matchId: api.match_id,
  homeScore: api.home_score,
  awayScore: api.away_score,
  qualifiedTeamId: api.qualified_team_id,
  penaltyShootout: api.penalty_shootout
    ? { home: api.penalty_shootout.home, away: api.penalty_shootout.away }
    : null,
  submittedAt: api.submitted_at,
  updatedAt: api.updated_at,
  points: api.points
    ? {
      total: api.points.total,
      exactScore: api.points.exact_score,
      correctWinner: api.points.correct_winner,
      correctQualifier: api.points.correct_qualifier,
      exactPenalty: api.points.exact_penalty,
    }
    : null,
});

const toRankingEntry = (api: ApiRankingEntry): RankingEntry => ({
  userId: api.user_id,
  rank: api.rank,
  rankDelta: api.rank_delta,
  firstName: api.first_name,
  lastName: api.last_name,
  avatarUrl: api.avatar_url ? resolveBackendUrl(api.avatar_url) : null,
  totalPoints: api.total_points,
  exactScores: api.exact_scores,
  correctWinners: api.correct_winners,
  isMe: api.is_me,
});

export const toRanking = (api: ApiRanking): Ranking => ({
  totalParticipants: api.total_participants,
  myEntry: api.my_entry ? toRankingEntry(api.my_entry) : null,
  entries: api.entries.map(toRankingEntry),
});

export const toLot = (api: ApiLot): Lot => ({
  id: api.id,
  name: api.name,
  description: api.description,
  rankPosition: api.rank_position,
  imageUrl: api.image_url ? resolveBackendUrl(api.image_url) : null,
});

export const toBadge = (api: ApiBadge): Badge => ({
  id: api.id,
  code: api.code,
  name: api.name,
  description: api.description,
  iconName: api.icon_name,
  isUnlocked: api.is_unlocked,
  unlockedAt: api.unlocked_at,
  progress: api.progress,
});

export const toMyPronosticsPreferences = (
  api: ApiMyPronosticsPreferences,
): MyPronosticsPreferences => ({
  emailDailyDigest: api.email_daily_digest,
  pushMatchReminder: api.push_match_reminder,
  isParticipating: api.is_participating,
  isMuted: api.is_muted,
  arePredictionsPublic: !api.visibility_opt_out,
});

export const fromPreferencePatch = (
  patch: PreferencePatch,
): ApiPreferencePatch => {
  const apiPatch: ApiPreferencePatch = {};
  if (patch.emailDailyDigest !== undefined) apiPatch.email_daily_digest = patch.emailDailyDigest;
  if (patch.pushMatchReminder !== undefined) apiPatch.push_match_reminder = patch.pushMatchReminder;
  if (patch.arePredictionsPublic !== undefined) {
    apiPatch.visibility_opt_out = !patch.arePredictionsPublic;
  }
  return apiPatch;
};

const toParticipantSummary = (api: ApiParticipantSummary): ParticipantSummary => ({
  userId: api.user_id,
  firstName: api.first_name,
  lastName: api.last_name,
  avatarUrl: api.avatar_url ? resolveBackendUrl(api.avatar_url) : null,
  rank: api.rank,
  totalPoints: api.total_points,
  exactScores: api.exact_scores,
  correctWinners: api.correct_winners,
});

export const toParticipantPredictionsView = (
  api: ApiParticipantPredictionsView,
): ParticipantPredictionsView => ({
  participant: toParticipantSummary(api.participant),
  predictions: api.predictions.map((item) => ({
    match: toMatch(item.match),
    prediction: item.prediction ? toPrediction(item.prediction) : null,
    pointsEarned: item.points_earned,
  })),
  visibilityOptOut: api.visibility_opt_out,
});

export const toMyStats = (api: ApiMyStats): MyStats => ({
  totalPoints: api.total_points,
  exactScores: api.exact_scores,
  correctWinners: api.correct_winners,
  correctQualifiers: api.correct_qualifiers,
  predictionsMade: api.predictions_made,
  predictionsTotal: api.predictions_total,
  accuracyPercent: api.accuracy_percent,
  rankHistory: api.rank_history.map((entry) => ({
    matchId: entry.match_id,
    matchDay: entry.match_day,
    pointsGained: entry.points_gained,
    rankAfter: entry.rank_after,
  })),
});
